/* eslint-disable ember/classic-decorator-hooks */
import EmberObject from '@ember/object';

import ActualFilterAmplitude from './actual-filter-amplitude';
import NoiseGroup from './noise-group';
import IdealOpAmp from './ideal-op-amp';
import BandPassInductorCircuit from './circuit';
import * as CircuitUtils from '../../../utils/circuit';
import * as CommonUtils from '../../../../utils/common';
import FilterConstants from '../../../../filter-constants';
import * as InductorBandPassCircuitLimitsUtils from './circuit-limits-utils';
import * as ComponentFactory from '../../../components/components-factory';
import { getImplementation } from '../../../gestures/optimization';
import { calculateFilterComponents } from '@ebiz/aditools-filter';
import { set } from '@ember/object';

// Encapsulates the creation of circuit components for stage properties and optimization
// A circuit is created from the stage properties stageFp, stageQ, and gain, and the optimization;
export default class InductorBandPass extends EmberObject {
  // Inputs
  stageFp;
  stageQ;
  gain;
  optimization;
  algorithm;

  // Process output - something used internally but on other classes
  supportedComponents;
  actualFilterAmplitudeModel;
  noiseGroupModel;
  idealOpAmpModel;
  circuitModel;
  idealOpAmpValuePaths;
  componentLimits;
  frequency;
  sizing;

  // Figures output - used internally and also as output in the figures
  opAmp;
  needReferenceStage = true;
  c1;
  r1;
  r3;
  r4;
  l1;
  masterCapacitor;
  allowGainChange;

  // For cGov params of calculateFilterComponents - aditools-filter
  c1IdealValue;

  init() {
    super.init();

    if (
      CommonUtils.isNullOrUndefined(this.stageFp) ||
      CommonUtils.isNullOrUndefined(this.stageQ) ||
      CommonUtils.isNullOrUndefined(this.gain) ||
      CommonUtils.isNullOrUndefined(this.optimization) ||
      CommonUtils.isNullOrUndefined(this.algorithm)
    ) {
      throw 'stageFp, stageQ, gain, optimization, and algorithm should not be null';
    }

    this.actualFilterAmplitudeModel = ActualFilterAmplitude;
    this.noiseGroupModel = NoiseGroup;
    this.idealOpAmpModel = IdealOpAmp;
    this.circuitModel = BandPassInductorCircuit;
    this.supportedComponents = ['c1', 'l1', 'r1', 'r3', 'r4'];
    this.idealOpAmpValuePaths = ['r1', 'r3', 'r4'];

    if (!CommonUtils.isNullOrUndefined(this.initialCapacitorValue)) {
      this.c1IdealValue = this._computeC1IdealValue(this.initialCapacitorValue);
    } else {
      this.c1IdealValue = this._computeC1IdealValue();
    }
    const idealValues = this._computeIdealValues();
    const componentLimits = InductorBandPassCircuitLimitsUtils.computeLimits(
      idealValues.c1IdealValue,
      idealValues.r1IdealValue,
      idealValues.l1IdealValue,
    );
    const gainResistorLimits = InductorBandPassCircuitLimitsUtils.computeGainResistorLimitMultipliers(
      idealValues.r3IdealValue,
      idealValues.r4IdealValue,
    );
    this._computeComponents(idealValues, componentLimits, gainResistorLimits);
  }

  _computeIdealValues() {
    const implementation = getImplementation(FilterConstants.implementationType.inductor);
    const rGain = this._computeR3AndR4().r3 + this._computeR3AndR4().r4;
    const c1IdealValue = this.c1IdealValue;
    const params = {
      type: this.algorithm,
      implementation: implementation,
      fp: this.stageFp,
      gain: this.gain,
      q: this.stageQ,
      cGov: c1IdealValue,
      rGainOrCCross: rGain,
    };
    const filterComponents = calculateFilterComponents(
      params.type,
      params.implementation,
      params.fp,
      params.gain,
      params.q,
      params.cGov,
      params.rGainOrCCross,
    );

    return {
      c1IdealValue: filterComponents.c1,
      c2IdealValue: filterComponents.c2,
      r1IdealValue: filterComponents.r1,
      r2IdealValue: filterComponents.r2,
      r3IdealValue: filterComponents.r3,
      r4IdealValue: filterComponents.r4,
      l1IdealValue: filterComponents.l1,
    };
  }

  _computeR3AndR4() {
    const r3PlusR4 = this.optimization.determineStartingR3R4Value(this.stageFp);
    return CircuitUtils.computeR3AndR4(r3PlusR4, this.gain);
  }

  _computeC1IdealValue(value) {
    return value ? value : CircuitUtils.computeMasterCapacitorValue(this.optimization, this.stageFp);
  }

  // Private
  _computeComponents(idealValues, componentLimits, gainResistorLimits) {
    let r3NominalValue = ComponentFactory.createR3(idealValues.r3IdealValue, null, null).toCommercialComponent().value;
    this.allowGainChange = CircuitUtils.getGainChangeSupportStatus(idealValues.r3IdealValue, idealValues.r4IdealValue);
    this.sizing = this._getSizing(idealValues, componentLimits);
    this.c1 = ComponentFactory.createC1MinAndMaxDefined(
      idealValues.c1IdealValue,
      componentLimits.c1Minimum,
      componentLimits.c1Maximum,
      this.sizing,
    );
    this.masterCapacitor = this.c1;
    let rcRatioInverse = CircuitUtils.getInverseOfMasterCapacitorRatio(this.c1);
    this.r1 = ComponentFactory.createR1MinAndMaxDefined(
      idealValues.r1IdealValue,
      componentLimits.r1Minimum,
      componentLimits.r1Maximum,
      this.sizing,
      rcRatioInverse,
    );
    this.r3 = ComponentFactory.createR3(
      r3NominalValue,
      gainResistorLimits.resistorMinimumLimitMultiplier,
      gainResistorLimits.resistorMaximumLimitMultiplier,
    );
    this.r4 = this._getR4(idealValues, gainResistorLimits);
    this.l1 = ComponentFactory.createInductor(
      idealValues.l1IdealValue,
      componentLimits.l1Minimum,
      componentLimits.l1Maximum,
      rcRatioInverse,
    );
    this.frequency = 1 / (2 * Math.PI * Math.sqrt(this.l1.value * this.c1.value));

    this.idealValues = idealValues;
    this.componentLimits = componentLimits;
    this.gainResistorLimits = gainResistorLimits;
  }

  // Private
  _getSizing(idealValues, componentLimits) {
    var r1Big = componentLimits.r1Maximum;
    var r1Tiny = componentLimits.r1Minimum;
    var r1IdealValue = idealValues.r1IdealValue;
    var result = null;
    if (r1IdealValue < r1Tiny) {
      result = FilterConstants.sizing.tiny;
    } else if (r1IdealValue > r1Big) {
      result = FilterConstants.sizing.big;
    }

    return result;
  }

  // Private
  // R4 may have an ideal value but it is recomputed here based on gain and R3
  // since it should adjust depending on the current value of R3.
  // (If r3 is not present or idealValue is 0 or INFINITY, we use the idealValue)
  // R3 may change due to the user changing the slider position or r3 adjusting to its nominal value.
  // R3 may also change when changing the e-series in the tolerance tab.
  _getR4(idealValues, gainResistorLimits) {
    let result = null;
    if (idealValues.r3IdealValue !== 0 && idealValues.r3IdealValue !== FilterConstants.Limits.INFINITY)
      result = ComponentFactory.createR4FromR3AndGain(
        this.r3.value,
        this.gain,
        gainResistorLimits.resistorMinimumLimitMultiplier,
        gainResistorLimits.resistorMaximumLimitMultiplier,
      );
    else
      result = ComponentFactory.createR4(
        idealValues.r4IdealValue,
        gainResistorLimits.resistorMinimumLimitMultiplier,
        gainResistorLimits.resistorMaximumLimitMultiplier,
      );

    return result;
  }

  changeGainValues(newR3Value) {
    if (this.allowGainChange) {
      this.r3 = this.r3.setValue(newR3Value);
      this.updateR4();
    }
  }

  updateR4() {
    set(this, 'r4', this._getR4(this.idealValues, this.gainResistorLimits));
  }

  changeInitialCapacitor(value) {
    this.c1IdealValue = this._computeC1IdealValue(value);
    const idealValues = this._computeIdealValues();
    const componentLimits = InductorBandPassCircuitLimitsUtils.computeLimits(
      idealValues.c1IdealValue,
      idealValues.r1IdealValue,
      idealValues.l1IdealValue,
    );
    const gainResistorLimits = InductorBandPassCircuitLimitsUtils.computeGainResistorLimitMultipliers(
      idealValues.r3IdealValue,
      idealValues.r4IdealValue,
    );
    this._computeComponents(idealValues, componentLimits, gainResistorLimits);
  }
}
